import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Brain,
  Briefcase,
  CheckCircle,
  ChevronRight,
  FileCheck,
  Lock,
  Network,
  Shield,
  Terminal,
  Users,
} from 'lucide-react'
import SEOOptimizer, { seoConfigs } from '../../components/SEOOptimizer'
import { trackEvent } from '../../utils/analytics'

import TopNavBar from '../components/TopNavBar'
import Footer from '../components/Footer'
import AnnouncementBar from '../components/AnnouncementBar'
import SocialProof from '../components/SocialProof'

const statHighlights = [
  { value: 'Local-first', label: 'Evidence storage', note: 'stays on your machine by default' },
  { value: 'Offline', label: 'AI option', note: 'no data leaves your network if you choose' },
  { value: '14 days', label: 'Free trial', note: 'no credit card required' },
]

const capabilityCards = [
  {
    icon: <Network className="h-5 w-5" />,
    title: 'No more guessing which path actually matters',
    copy: 'Attack paths and identity risk get mapped as you go, so the finding that needs action is never buried under twenty that don’t.',
  },
  {
    icon: <Brain className="h-5 w-5" />,
    title: 'AI drafts, your team still signs off',
    copy: 'Local or hosted AI speeds up triage and write-up. Every call still needs a human before it ships; nothing goes out unreviewed.',
  },
  {
    icon: <FileCheck className="h-5 w-5" />,
    title: 'Stop rebuilding the report from memory',
    copy: 'Commands, screenshots, and remediation notes stay attached to the finding the moment you make it, so the report is ready when the testing ends.',
  },
]

const audienceCards = [
  {
    icon: <Users className="h-5 w-5" />,
    label: 'Internal security teams',
    title: 'Know what was tested and what evidence supports it.',
    copy: 'For IT and security leads at 50 to 500 person companies who need control, proof, and board-ready clarity.',
    route: '/solutions/internal-security-teams',
  },
  {
    icon: <Briefcase className="h-5 w-5" />,
    label: 'Pentest firms',
    title: 'Stop rebuilding the story at reporting time.',
    copy: 'Keep engagement context, evidence, review notes, and final reporting in one client-ready project record.',
    route: '/solutions/pentest-firms',
  },
]

const comparisonRows = [
  { label: 'Core role', nodeZero: 'Autonomous, automated attack-path validation', zypheron: 'Operator-driven assessment workspace' },
  { label: 'Best fit', nodeZero: 'Continuous, unattended internal pentesting', zypheron: 'Hands-on engagements led by a human operator' },
  { label: 'Scope', nodeZero: 'Runs a fixed autonomous attack path', zypheron: 'Whole engagement, recon through report, operator-directed' },
  { label: 'Data posture', nodeZero: 'Cloud-orchestrated by design', zypheron: 'Local-first desktop with offline AI options' },
  { label: 'Final output', nodeZero: 'Automated findings and proof-of-exploit', zypheron: 'Board/client-ready assessment record' },
  { label: 'Reporting', nodeZero: 'Auto-generated from the automated run', zypheron: 'Evidence tied to findings as you go, exported on demand' },
]

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const heroVideoRef = useRef<HTMLVideoElement>(null)

  // Autoplay only after mount, and only when the visitor has not asked for
  // reduced motion. Reduced-motion visitors see the poster frame and can
  // press play themselves via the native controls.
  useEffect(() => {
    const video = heroVideoRef.current
    if (!video) return
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      video.controls = true
      return
    }
    video.play().catch(() => {
      video.controls = true
    })
  }, [])

  return (
    <>
      <SEOOptimizer {...seoConfigs.home} />

      <div className="fixed inset-0 bg-void z-0 pointer-events-none">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-radial from-transparent to-void opacity-90"></div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <AnnouncementBar
          id="2026-08-hero-refresh"
          text="New: Zypheron pricing is now trial-first, no card needed to start."
          ctaLabel="See what's new"
          to="/changelog"
        />
        <TopNavBar />

        <section className="px-6 pb-24 pt-28 lg:px-12 lg:pb-32 lg:pt-36">
          <div className="mx-auto grid max-w-[1280px] items-center gap-16 lg:grid-cols-[0.86fr_1.14fr]">
            <div>
              <div className="mb-7 inline-flex items-center gap-2 border border-zypheron-500/20 bg-zypheron-500/10 px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.22em] text-zypheron-400">
                <Shield className="h-3 w-3" />
                Test. Prove it. Report.
              </div>

              <h1 className="mb-7 max-w-4xl text-4xl font-bold leading-[1.06] tracking-tight text-white font-display md:text-5xl lg:text-6xl">
                The pentest workspace where the report writes itself as you work.
              </h1>

              <p className="mb-4 max-w-2xl text-lg font-semibold leading-8 text-zypheron-400 md:text-xl">
                Semi-automated, human-led pentesting for pentest firms and internal security teams.
              </p>
              <p className="mb-9 max-w-2xl text-base leading-7 text-gray-300 md:text-lg">
                AI handles recon, triage, evidence capture, and the first draft of the report. You make every call, and nothing runs until you approve it.
              </p>

              <div className="mb-8 flex max-w-2xl flex-col items-start gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={() => {
                    trackEvent('trial_cta_click', { surface: 'home_hero_primary' })
                    navigate('/billing/ide')
                  }}
                  className="btn-primary inline-flex min-w-[280px] items-center justify-center gap-2 px-10 py-5 text-base"
                >
                  Start 14-day trial
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    trackEvent('hero_download_click', { surface: 'home_hero_secondary' })
                    navigate('/download')
                  }}
                  className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3"
                >
                  <Terminal className="h-4 w-4" />
                  Download Desktop
                </button>
              </div>

              <div className="mb-9 text-xs text-gray-500">
                No credit card required &middot; 14-day free trial &middot; Cancel anytime
              </div>

              <div className="grid max-w-xl gap-3 text-sm text-gray-400 sm:grid-cols-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-zypheron-500" />
                  Local-first
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-zypheron-500" />
                  Offline AI option
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-zypheron-500" />
                  Report-ready
                </div>
              </div>
            </div>

            <div className="relative lg:-mr-4">
              <div className="absolute -inset-8 rounded-full bg-zypheron-500/5 blur-3xl"></div>
              <figure className="relative border border-white/10 bg-white/[0.025] p-2 shadow-2xl shadow-black/60">
                <video
                  ref={heroVideoRef}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/videos/zypheron-product-demo.webp"
                  aria-label="Zypheron Desktop mapping attack paths and running assessment commands"
                  className="block aspect-video w-full rounded-[4px] border border-zypheron-500/20 object-cover"
                >
                  <source src="/videos/zypheron-product-demo.webm" type="video/webm" />
                  <source src="/videos/zypheron-product-demo.mp4" type="video/mp4" />
                </video>
                <figcaption className="px-2 pb-1 pt-3 text-center text-xs text-gray-500">
                  Real attack paths mapped as you work. 14-day trial, no card.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-surface/70 px-6 py-14 lg:px-12">
          <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-3">
            {statHighlights.map((stat) => (
              <div key={stat.label} className="text-center sm:text-left">
                <div className="text-4xl font-bold tracking-tight text-white font-display">{stat.value}</div>
                <div className="mt-2 text-sm font-semibold text-gray-300">{stat.label}</div>
                <div className="mt-1 text-xs text-gray-500">{stat.note}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-white/5 bg-surface/70 px-6 py-20 lg:px-12">
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
            <div className="md:col-span-1">
              <div className="mb-3 text-xs font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">What it replaces</div>
              <h2 className="text-3xl font-bold tracking-tight text-white font-display">Less scattered work. More proof.</h2>
            </div>
            <div className="grid gap-4 md:col-span-2 md:grid-cols-3">
              {capabilityCards.map((card) => (
                <div key={card.title} className="border border-white/10 bg-void/80 p-6">
                  <div className="mb-5 flex h-10 w-10 items-center justify-center border border-zypheron-500/20 bg-zypheron-500/10 text-zypheron-400">
                    {card.icon}
                  </div>
                  <h3 className="mb-3 text-lg font-semibold text-white">{card.title}</h3>
                  <p className="text-sm leading-6 text-gray-400">{card.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-void px-6 py-28 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 max-w-3xl">
              <div className="mb-3 text-xs font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">Built for practical security teams</div>
              <h2 className="mb-5 text-3xl font-bold tracking-tight text-white font-display md:text-5xl">A calmer way to run internal assessments.</h2>
              <p className="text-lg leading-8 text-gray-400">
                Zypheron keeps the workflow focused: scope the work, map the paths, preserve evidence, review findings, and produce a clean report leadership or clients can act on.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {audienceCards.map((card) => (
                <button
                  key={card.title}
                  onClick={() => navigate(card.route)}
                  className="group border border-white/10 bg-panel/80 p-8 text-left transition-colors hover:border-zypheron-500/40"
                >
                  <div className="mb-7 flex items-center gap-3 text-zypheron-400">
                    {card.icon}
                    <span className="text-xs font-mono-tech uppercase tracking-[0.2em]">{card.label}</span>
                  </div>
                  <h3 className="mb-4 max-w-xl text-2xl font-bold leading-tight text-white font-display">{card.title}</h3>
                  <p className="mb-8 max-w-xl leading-7 text-gray-400">{card.copy}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-[0.14em] text-white group-hover:text-zypheron-400">
                    View team fit <ChevronRight className="h-4 w-4" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-surface px-6 py-28 lg:px-12">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <div className="mb-3 text-xs font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">Zypheron vs NodeZero</div>
              <h2 className="mb-5 text-3xl font-bold tracking-tight text-white font-display md:text-5xl">Built for engagements an operator runs.</h2>
              <p className="mb-6 text-lg leading-8 text-gray-400">
                NodeZero runs autonomous, unattended attack-path testing. Zypheron is for teams that want an operator in the loop: a controlled desktop workspace for hands-on AD, cloud, network recon, evidence, AI assistance, and reporting.
              </p>
              <div className="flex items-start gap-3 border border-white/10 bg-void/70 p-5 text-sm leading-6 text-gray-400">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-400" />
                Keep sensitive assessment data local when the environment calls for it.
              </div>
            </div>

            <div className="overflow-hidden border border-white/10 bg-void/70">
              <div className="grid grid-cols-3 border-b border-white/10 bg-white/5 p-4 text-xs uppercase tracking-[0.16em] text-gray-500 font-mono-tech">
                <div>Area</div>
                <div className="text-center">NodeZero</div>
                <div className="text-center text-zypheron-400">Zypheron</div>
              </div>
              {comparisonRows.map((row) => (
                <div key={row.label} className="grid grid-cols-3 gap-3 border-b border-white/5 p-4 text-sm last:border-b-0">
                  <div className="font-semibold text-gray-300">{row.label}</div>
                  <div className="text-center leading-6 text-gray-500">{row.nodeZero}</div>
                  <div className="text-center leading-6 font-medium text-white">{row.zypheron}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-void px-6 py-24 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-3 text-xs font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">Private System: by qualification</div>
            <h2 className="mb-5 text-3xl font-bold tracking-tight text-white font-display md:text-4xl">
              For teams that want us in the room with them.
            </h2>
            <p className="mb-8 text-lg leading-8 text-gray-400">
              A short working engagement: we install a complete, controlled assessment and reporting system
              inside Zypheron around your process: evidence standards, review rules, reporting templates,
              operator-controlled AI, self-hosted or air-gapped where required, then train your team and
              hand it over. Limited slots, so it starts with a few questions.
            </p>
            <button
              onClick={() => { trackEvent('qualify_cta_click', { surface: 'home_private_system' }); navigate('/qualify') }}
              className="btn-secondary inline-flex items-center justify-center gap-2 px-7 py-3.5"
            >
              <Lock className="h-4 w-4" />
              See if you qualify
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="mt-4">
              <button
                onClick={() => { trackEvent('proof_resources_link_click', { surface: 'home_private_system_playbook' }); navigate('/blog/zypheron-methodology') }}
                className="text-sm text-gray-400 underline-offset-4 hover:text-white hover:underline"
              >
                Read the methodology first
              </button>
            </div>
          </div>
        </section>

        <SocialProof />

        <section className="bg-void px-6 py-28 lg:px-12" id="pricing-section">
          <div className="mx-auto max-w-6xl">
            <div className="mb-14 max-w-3xl">
              <div className="mb-3 text-xs font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">Pricing</div>
              <h2 className="mb-5 text-3xl font-bold tracking-tight text-white font-display md:text-5xl">Try it on a real engagement before you pay for it.</h2>
              <p className="text-lg leading-8 text-gray-400">
                The open-source CLI is free. Every paid plan gets a 14-day trial with no card on file, enough to run one real assessment and see if the report writes itself.
              </p>
            </div>

            <div className="mb-14 flex flex-wrap gap-x-10 gap-y-3 border-y border-white/5 py-6 text-sm text-gray-400">
              {statHighlights.map((stat) => (
                <div key={stat.label} className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-white font-display">{stat.value}</span>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="border border-zypheron-500/40 bg-panel p-8 shadow-2xl shadow-zypheron-500/10">
                <div className="mb-8">
                  <div className="mb-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-zypheron-400">Operator</div>
                  <div className="text-2xl font-bold text-white font-display">Run your own engagements, evidence attached</div>
                  <p className="mt-3 text-sm text-gray-500">Full desktop access. 14-day free trial, no card required.</p>
                </div>
                <ul className="mb-8 space-y-4 text-sm text-gray-300">
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> IDE workspace, editor, terminal, and evidence record</li>
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> AD, identity, cloud recon, and reverse engineering surfaces</li>
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Local on-device AI (Ollama) and auto-updates</li>
                </ul>
                <div className="mb-4 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-white font-display">$99.99</span>
                  <span className="text-sm text-gray-500">/mo</span>
                </div>
                <button onClick={() => { trackEvent('pricing_cta_click', { plan: 'ide' }); navigate('/billing/ide') }} className="w-full bg-zypheron-500 py-4 text-xs font-bold font-mono-tech uppercase tracking-[0.16em] text-black transition-colors hover:bg-zypheron-400">
                  Start 14-day trial
                </button>
              </div>

              <div className="border border-white/10 bg-panel/80 p-8">
                <div className="mb-8">
                  <div className="mb-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-gray-500">Team</div>
                  <div className="text-2xl font-bold text-white font-display">Stop being the one who translates findings for the board</div>
                  <p className="mt-3 text-sm text-gray-500">Pairs well with the IDE and also runs on its own. 14-day free trial, no card required.</p>
                </div>
                <ul className="mb-8 space-y-4 text-sm text-white">
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Team visibility across assessments</li>
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Evidence-backed reporting in Cloud</li>
                  <li className="flex gap-3"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Shared reporting for managers, clients, and operators</li>
                </ul>
                <button onClick={() => { trackEvent('pricing_cta_click', { plan: 'cloud' }); navigate('/billing/cloud') }} className="w-full border border-white/10 bg-white/5 py-4 text-xs font-mono-tech uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10">
                  Start Team trial
                </button>
              </div>

              <div className="border border-white/10 bg-panel/80 p-8">
                <div className="mb-8">
                  <div className="mb-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-gray-500">Private System</div>
                  <div className="text-3xl font-bold text-white font-display">Custom engagement</div>
                  <p className="mt-3 text-sm text-gray-500">For teams that need a controlled assessment and reporting system, self-hosted or air-gapped options, and private setup.</p>
                </div>
                <ul className="mb-8 space-y-4 text-sm text-gray-300">
                  <li className="flex gap-3"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Workflow and reporting standard built to your process</li>
                  <li className="flex gap-3"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Private onboarding and methodology alignment</li>
                  <li className="flex gap-3"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-500" /> Self-hosted / air-gap available on request</li>
                </ul>
                <button onClick={() => { trackEvent('pricing_cta_click', { plan: 'private-system' }); navigate('/qualify') }} className="w-full border border-white/10 bg-white/5 py-4 text-xs font-mono-tech uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10">
                  See if you qualify
                </button>
              </div>
            </div>

            <p className="mt-8 text-sm text-gray-500">
              Wherever your team is based, billing is handled at checkout in your local currency where supported.
            </p>
            <p className="mt-3 text-sm text-gray-500">
              Have questions? Check the{' '}
              <button onClick={() => { trackEvent('faq_link_click', { surface: 'pricing_section' }); navigate('/faq') }} className="text-zypheron-400 underline-offset-4 hover:underline">
                FAQ
              </button>{' '}
              or see{' '}
              <button onClick={() => { trackEvent('proof_resources_link_click', { surface: 'pricing_section' }); navigate('/resources/case-studies') }} className="text-zypheron-400 underline-offset-4 hover:underline">
                proof and evaluation resources
              </button>
              .
            </p>
          </div>
        </section>

        <section className="border-t border-white/5 bg-surface/70 px-6 py-24 lg:px-12">
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h2 className="mb-4 text-3xl font-bold tracking-tight text-white font-display md:text-4xl">Your next report shouldn't cost you a Friday.</h2>
              <p className="text-lg leading-8 text-gray-400">Start the 14-day no-card trial, run one engagement in Zypheron Desktop, and see what's already written by the time you'd normally start.</p>
            </div>
            <div className="flex shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center">
              <button
                onClick={() => {
                  trackEvent('trial_cta_click', { surface: 'home_footer_cta' })
                  navigate('/billing/ide')
                }}
                className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5"
              >
                Start 14-day trial
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  trackEvent('hero_download_click', { surface: 'home_footer_secondary' })
                  navigate('/download')
                }}
                className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3"
              >
                <Terminal className="h-4 w-4" />
                Download Desktop
              </button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

export default LandingPage
